import React, { useState, useEffect } from 'react';
import { Plus, Trash2, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import DataTable from '../../components/admin/DataTable';
import ImageUpload from '../../components/admin/ImageUpload';
import FileUpload from '../../components/admin/FileUpload';
import RichTextEditor from '../../components/admin/RichTextEditor';

const DepartmentsManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '', slug: '', category: 'إدارة', icon: 'Building',
    description: '', about: '', vision: '', mission: '', objectives: '',
    responsibilities: [], 
    forms: [],
    programs: [],
    regulations: [],
    annual_reports: [],
    director_name: '', director_title: '', director_degree: '', director_message: '',
    director_image: '', director_cv_url: '',
    image: '', background_image: '',
    email: '', phone: '', location: '',
    is_published: true, order: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/departments/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      setItems(await response.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `${BACKEND_URL}/api/departments/${editing.id}` : `${BACKEND_URL}/api/departments/`;
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        fetchItems();
        resetForm();
        alert('تم الحفظ بنجاح');
      }
    } catch (error) {
      alert('حدث خطأ');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', slug: '', category: 'إدارة', icon: 'Building',
      description: '', about: '', vision: '', mission: '', objectives: '',
      responsibilities: [],
      forms: [],
      programs: [],
      regulations: [],
      annual_reports: [],
      director_name: '', director_title: '', director_degree: '', director_message: '',
      director_image: '', director_cv_url: '',
      image: '', background_image: '',
      email: '', phone: '', location: '',
      is_published: true, order: 0
    });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setFormData({
      name: item.name || '',
      slug: item.slug || '',
      category: item.category || 'إدارة',
      icon: item.icon || 'Building',
      description: item.description || '',
      about: item.about || '',
      vision: item.vision || '',
      mission: item.mission || '',
      objectives: item.objectives || '',
      responsibilities: item.responsibilities || [],
      forms: (item.forms || []).map(f => ({
        title_ar: f.title_ar || f.title || '',
        title_en: f.title_en || '',
        file_url: f.file_url || f.url || ''
      })),
      programs: (item.programs || []).map(p => ({
        title_ar: p.title_ar || p.title || '',
        title_en: p.title_en || '',
        link: p.link || p.url || ''
      })),
      regulations: (item.regulations || []).map(r => ({
        title_ar: r.title_ar || r.title || '',
        title_en: r.title_en || '',
        file_url: r.file_url || r.url || ''
      })),
      annual_reports: (item.annual_reports || []).map(a => ({
        title_ar: a.title_ar || a.title || '',
        title_en: a.title_en || '',
        file_url: a.file_url || a.url || '',
        year: a.year || ''
      })),
      director_name: item.director_name || '',
      director_title: item.director_title || '',
      director_degree: item.director_degree || '',
      director_message: item.director_message || '',
      director_image: item.director_image || '',
      director_cv_url: item.director_cv_url || '',
      image: item.image || '',
      background_image: item.background_image || '',
      email: item.email || '',
      phone: item.phone || '',
      location: item.location || '',
      is_published: item.is_published !== false,
      order: item.order || 0
    });
    setShowForm(true);
  };

  // Functions for managing document sections
  const addForm = () => {
    setFormData({
      ...formData,
      forms: [...formData.forms, { title_ar: '', title_en: '', file_url: '' }]
    });
  };

  const updateForm = (index, field, value) => {
    const newForms = [...formData.forms];
    newForms[index][field] = value;
    setFormData({ ...formData, forms: newForms });
  };

  const removeForm = (index) => {
    setFormData({
      ...formData,
      forms: formData.forms.filter((_, i) => i !== index)
    });
  };

  const addProgram = () => {
    setFormData({
      ...formData,
      programs: [...formData.programs, { title_ar: '', title_en: '', link: '' }]
    });
  };

  const updateProgram = (index, field, value) => {
    const newPrograms = [...formData.programs];
    newPrograms[index][field] = value;
    setFormData({ ...formData, programs: newPrograms });
  };

  const removeProgram = (index) => {
    setFormData({
      ...formData,
      programs: formData.programs.filter((_, i) => i !== index)
    });
  };

  const addRegulation = () => {
    setFormData({
      ...formData,
      regulations: [...formData.regulations, { title_ar: '', title_en: '', file_url: '' }]
    });
  };

  const updateRegulation = (index, field, value) => {
    const newRegulations = [...formData.regulations];
    newRegulations[index][field] = value;
    setFormData({ ...formData, regulations: newRegulations });
  };

  const removeRegulation = (index) => {
    setFormData({
      ...formData,
      regulations: formData.regulations.filter((_, i) => i !== index)
    });
  };

  const addAnnualReport = () => {
    setFormData({
      ...formData,
      annual_reports: [...formData.annual_reports, { title_ar: '', title_en: '', file_url: '', year: '' }]
    });
  };

  const updateAnnualReport = (index, field, value) => {
    const newReports = [...formData.annual_reports];
    newReports[index][field] = value;
    setFormData({ ...formData, annual_reports: newReports });
  };

  const removeAnnualReport = (index) => {
    setFormData({
      ...formData,
      annual_reports: formData.annual_reports.filter((_, i) => i !== index)
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من الحذف؟')) return;
    try {
      await fetch(`${BACKEND_URL}/api/departments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      fetchItems();
    } catch (error) {
      alert('حدث خطأ في الحذف');
    }
  };

  const columns = [
    { header: 'الاسم', accessor: 'name' },
    { header: 'النوع', accessor: 'category' },
    { header: 'Slug', accessor: 'slug' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#0F388E]">إدارة الإدارات والمراكز</h1>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#0F388E]">
          <Plus className="ml-2" />إضافة إدارة/مركز
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader><CardTitle>{editing ? 'تعديل' : 'إضافة'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">النوع *</label>
                  <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg">
                    <option value="إدارة">إدارة</option>
                    <option value="مركز">مركز</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الأيقونة</label>
                  <select value={formData.icon} onChange={(e) => setFormData({...formData, icon: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg">
                    <option value="Building">Building</option>
                    <option value="Building2">Building2</option>
                    <option value="Monitor">Monitor</option>
                    <option value="Megaphone">Megaphone</option>
                    <option value="DollarSign">DollarSign</option>
                    <option value="GraduationCap">GraduationCap</option>
                    <option value="Microscope">Microscope</option>
                    <option value="Heart">Heart</option>
                    <option value="Award">Award</option>
                    <option value="BookOpen">BookOpen</option>
                    <option value="Wrench">Wrench</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">الاسم *</label>
                <input type="text" placeholder="الاسم" value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" required />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Slug (للرابط) *</label>
                <input type="text" placeholder="general-secretariat" value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" required />
                <p className="text-xs text-gray-500 mt-1">مثال: general-secretariat, it-department</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">الوصف *</label>
                <textarea value={formData.description} rows={3}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" required />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">الرؤية</label>
                <textarea value={formData.vision} rows={2}
                  onChange={(e) => setFormData({...formData, vision: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">الرسالة</label>
                <textarea value={formData.mission} rows={2}
                  onChange={(e) => setFormData({...formData, mission: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">الأهداف</label>
                <textarea value={formData.objectives} rows={2}
                  onChange={(e) => setFormData({...formData, objectives: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg" />
              </div>
              
              {/* صور الإدارة/المركز */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-bold text-[#0F388E] mb-4">صور الإدارة/المركز</h3>
                <div className="grid grid-cols-2 gap-4">
                  <ImageUpload 
                    label="صورة خلفية الإدارة/المركز" 
                    value={formData.background_image}
                    onChange={(v) => setFormData({...formData, background_image: v})} />
                  <ImageUpload 
                    label="صورة الإدارة/المركز (أيقونة)" 
                    value={formData.image}
                    onChange={(v) => setFormData({...formData, image: v})} />
                </div>
              </div>
              
              {/* معلومات المدير */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-bold text-[#0F388E] mb-4">معلومات المدير</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">اسم المدير</label>
                    <input type="text" value={formData.director_name}
                      onChange={(e) => setFormData({...formData, director_name: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg" 
                      placeholder="د. محمد أحمد" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">الصفة/المنصب</label>
                      <input type="text" value={formData.director_title}
                        onChange={(e) => setFormData({...formData, director_title: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg" 
                        placeholder="مدير الإدارة" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">الدرجة العلمية</label>
                      <input type="text" value={formData.director_degree}
                        onChange={(e) => setFormData({...formData, director_degree: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg" 
                        placeholder="دكتوراه في علوم الحاسوب" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">رسالة المدير</label>
                    <textarea value={formData.director_message} rows={3}
                      onChange={(e) => setFormData({...formData, director_message: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg" 
                      placeholder="رسالة ترحيبية من المدير..." />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <ImageUpload 
                      label="صورة المدير" 
                      value={formData.director_image}
                      onChange={(v) => setFormData({...formData, director_image: v})} />
                    <FileUpload 
                      label="السيرة الذاتية (PDF)" 
                      value={formData.director_cv_url}
                      onChange={(v) => setFormData({...formData, director_cv_url: v})} 
                      accept=".pdf" />
                  </div>
                </div>
              </div>
              
              {/* نماذج واستمارات */}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-[#0F388E]">📄 نماذج واستمارات (PDF)</h3>
                  <Button type="button" onClick={addForm} size="sm" className="bg-[#C9A040]">
                    <Plus className="ml-2 h-4 w-4" />إضافة نموذج
                  </Button>
                </div>
                {formData.forms.map((form, index) => (
                  <Card key={index} className="mb-3 bg-gray-50">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">العنوان بالعربي *</label>
                          <input
                            type="text"
                            value={form.title_ar}
                            onChange={(e) => updateForm(index, 'title_ar', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="نموذج التسجيل"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">العنوان بالإنجليزي</label>
                          <input
                            type="text"
                            value={form.title_en}
                            onChange={(e) => updateForm(index, 'title_en', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="Registration Form"
                          />
                        </div>
                      </div>
                      <FileUpload
                        label="ملف PDF"
                        value={form.file_url}
                        onChange={(v) => updateForm(index, 'file_url', v)}
                        accept=".pdf"
                      />
                      <Button
                        type="button"
                        onClick={() => removeForm(index)}
                        variant="destructive"
                        size="sm"
                        className="mt-2"
                      >
                        <Trash2 className="ml-2 h-4 w-4" />حذف
                      </Button>
                    </CardContent>
                  </Card>
                ))}
                {formData.forms.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">لا توجد نماذج. اضغط &quot;إضافة نموذج&quot; لإضافة نموذج جديد</p>
                )}
              </div>

              {/* برامج ومبادرات */}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-[#0F388E]">🎯 برامج ومبادرات (روابط خارجية)</h3>
                  <Button type="button" onClick={addProgram} size="sm" className="bg-[#C9A040]">
                    <Plus className="ml-2 h-4 w-4" />إضافة برنامج
                  </Button>
                </div>
                {formData.programs.map((program, index) => (
                  <Card key={index} className="mb-3 bg-gray-50">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">العنوان بالعربي *</label>
                          <input
                            type="text"
                            value={program.title_ar}
                            onChange={(e) => updateProgram(index, 'title_ar', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="برنامج التميز الأكاديمي"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">العنوان بالإنجليزي</label>
                          <input
                            type="text"
                            value={program.title_en}
                            onChange={(e) => updateProgram(index, 'title_en', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="Academic Excellence Program"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">رابط البرنامج *</label>
                        <input
                          type="url"
                          value={program.link}
                          onChange={(e) => updateProgram(index, 'link', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                          placeholder="https://example.com/program"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={() => removeProgram(index)}
                        variant="destructive"
                        size="sm"
                        className="mt-2"
                      >
                        <Trash2 className="ml-2 h-4 w-4" />حذف
                      </Button>
                    </CardContent>
                  </Card>
                ))}
                {formData.programs.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">لا توجد برامج. اضغط &quot;إضافة برنامج&quot; لإضافة برنامج جديد</p>
                )}
              </div>

              {/* لوائح وأدلة */}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-[#0F388E]">📚 لوائح وأدلة (PDF)</h3>
                  <Button type="button" onClick={addRegulation} size="sm" className="bg-[#C9A040]">
                    <Plus className="ml-2 h-4 w-4" />إضافة لائحة
                  </Button>
                </div>
                {formData.regulations.map((regulation, index) => (
                  <Card key={index} className="mb-3 bg-gray-50">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">العنوان بالعربي *</label>
                          <input
                            type="text"
                            value={regulation.title_ar}
                            onChange={(e) => updateRegulation(index, 'title_ar', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="لائحة الطلاب"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">العنوان بالإنجليزي</label>
                          <input
                            type="text"
                            value={regulation.title_en}
                            onChange={(e) => updateRegulation(index, 'title_en', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="Student Regulations"
                          />
                        </div>
                      </div>
                      <FileUpload
                        label="ملف PDF"
                        value={regulation.file_url}
                        onChange={(v) => updateRegulation(index, 'file_url', v)}
                        accept=".pdf"
                      />
                      <Button
                        type="button"
                        onClick={() => removeRegulation(index)}
                        variant="destructive"
                        size="sm"
                        className="mt-2"
                      >
                        <Trash2 className="ml-2 h-4 w-4" />حذف
                      </Button>
                    </CardContent>
                  </Card>
                ))}
                {formData.regulations.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">لا توجد لوائح. اضغط &quot;إضافة لائحة&quot; لإضافة لائحة جديدة</p>
                )}
              </div>

              {/* التقارير السنوية */}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-[#0F388E]">📊 التقارير السنوية (PDF)</h3>
                  <Button type="button" onClick={addAnnualReport} size="sm" className="bg-[#C9A040]">
                    <Plus className="ml-2 h-4 w-4" />إضافة تقرير
                  </Button>
                </div>
                {formData.annual_reports.map((report, index) => (
                  <Card key={index} className="mb-3 bg-gray-50">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">العنوان بالعربي *</label>
                          <input
                            type="text"
                            value={report.title_ar}
                            onChange={(e) => updateAnnualReport(index, 'title_ar', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="التقرير السنوي 2024"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">العنوان بالإنجليزي</label>
                          <input
                            type="text"
                            value={report.title_en}
                            onChange={(e) => updateAnnualReport(index, 'title_en', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="Annual Report 2024"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">السنة *</label>
                          <input
                            type="text"
                            value={report.year}
                            onChange={(e) => updateAnnualReport(index, 'year', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                            placeholder="2024"
                          />
                        </div>
                      </div>
                      <FileUpload
                        label="ملف PDF"
                        value={report.file_url}
                        onChange={(v) => updateAnnualReport(index, 'file_url', v)}
                        accept=".pdf"
                      />
                      <Button
                        type="button"
                        onClick={() => removeAnnualReport(index)}
                        variant="destructive"
                        size="sm"
                        className="mt-2"
                      >
                        <Trash2 className="ml-2 h-4 w-4" />حذف
                      </Button>
                    </CardContent>
                  </Card>
                ))}
                {formData.annual_reports.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">لا توجد تقارير. اضغط &quot;إضافة تقرير&quot; لإضافة تقرير جديد</p>
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">البريد الإلكتروني</label>
                  <input type="email" value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الهاتف</label>
                  <input type="text" value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الترتيب</label>
                  <input type="number" value={formData.order}
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2 border rounded-lg" />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_published" checked={formData.is_published}
                  onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                  className="w-4 h-4" />
                <label htmlFor="is_published" className="text-sm font-medium">منشور</label>
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={resetForm}>إلغاء</Button>
                <Button type="submit">حفظ</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
            </div>
          ) : (
            <DataTable columns={columns} data={items}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={loading} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DepartmentsManagement;
